/**
 * AppLayout — Main authenticated layout wrapper
 *
 * Full-width header + Sidebar (collapsible) + Main content area.
 * Uses <Outlet /> for nested route rendering.
 */

import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { MobileSidebar } from './MobileSidebar';
import { EntryCloseMobileBar } from './EntryCloseHeaderIndicator';
import { useIsAuthenticated } from '../../state_management_and_stores/auth.store';
import { BreadcrumbProvider } from '../../contexts/BreadcrumbContext';
import { cn } from '../../lib/utils';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  return (
    <BreadcrumbProvider>
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} />

      {/* Full-width header — spans entire viewport */}
      <AppHeader onMobileMenuToggle={() => setMobileOpen(true)} />

      {/* Mobile-only "entries closing soon" bar — sticky directly below the
          header at <md widths. The desktop equivalent renders inline inside
          AppHeader. Self-hides when nothing's closing soon. Gated on auth
          for the same reason as the header indicator: the underlying query
          would otherwise fire on public/login routes. */}
      {isAuthenticated && <EntryCloseMobileBar />}

      {/* Body: sidebar + content */}
      <div className="flex">
        {/* Desktop sidebar — below header */}
        <aside
          className={cn(
            'fixed top-14 bottom-0 left-0 z-30 hidden lg:flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200',
            collapsed ? 'w-16' : 'w-64'
          )}
        >
          <AppSidebar
            collapsed={collapsed}
            onToggleCollapse={() => setCollapsed(!collapsed)}
          />
        </aside>

        {/* Main content area — sidebar offset wrapper + centered content */}
        <div
          className={cn(
            'flex-1 min-h-[calc(100vh-3.5rem)] transition-all duration-200',
            collapsed ? 'lg:pl-16' : 'lg:pl-64'
          )}
        >
          <main className="p-4 md:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
    </BreadcrumbProvider>
  );
}
